import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    // Safely parse localStorage data
    const getStoredUser = () => {
        try {
            const storedUser = localStorage.getItem('user');
            if (storedUser && storedUser !== 'undefined') {
                return JSON.parse(storedUser);
            }
        } catch (error) {
            console.error('Error parsing stored user:', error);
            localStorage.removeItem('user'); // Clean up invalid data
        }
        return null;
    };

    const getStoredToken = () => {
        const token = localStorage.getItem('token');
        // Check if token exists and is not "undefined" string
        return token && token !== 'undefined' ? token : null;
    };

    const [user, setUser] = useState(() => getStoredUser());
    const [token, setToken] = useState(() => getStoredToken());
    const [loading, setLoading] = useState(true);

    // Initialize axios with base URL
    useEffect(() => {
        axios.defaults.baseURL = import.meta.env.VITE_API_URL;
    }, []);

    // Set up axios interceptors
    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
    }, [token]);

    // Check initial authentication on mount
    useEffect(() => {
        const checkAuth = async () => {
            const storedToken = getStoredToken();
            const storedUser = getStoredUser();

            if (storedToken && storedUser) {
                try {
                    // Verify token with server if needed
                    // Remove or comment out this verification if endpoint doesn't exist
                    // await axios.get('/auth/verify');
                    setToken(storedToken);
                    setUser(storedUser);
                } catch (error) {
                    console.error('Token verification failed:', error);
                    // Token is invalid, clear storage
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    setToken(null);
                    setUser(null);
                }
            }
            setLoading(false);
        };

        checkAuth();
    }, []);

    const login = useCallback(async (username, password) => {
        setLoading(true);
        try {
            const response = await axios.post('/auth/login', {
                username,
                password
            });

            if (response.data.status === 'success') {
                const { token, user } = response.data.data;

                // Store in state
                setToken(token);
                setUser(user);

                // Store in localStorage
                localStorage.setItem('token', token);
                localStorage.setItem('user', JSON.stringify(user));

                // Set axios default headers
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                setLoading(false);
                return { success: true, data: response.data.data };
            } else {
                setLoading(false);
                return {
                    success: false,
                    error: response.data.message || 'Login failed'
                };
            }
        } catch (error) {
            console.error('Login error:', error);
            setLoading(false);

            let errorMessage = 'An error occurred. Please try again.';

            if (error.response) {
                errorMessage = error.response.data?.message || 'Login failed. Please check your credentials.';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            }

            return {
                success: false,
                error: errorMessage
            };
        }
    }, []);

    const logout = useCallback(() => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        // Use navigate if available, otherwise reload
        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
    }, []);

    const isAuthenticated = !!token && !!user;

    const value = {
        user,
        token,
        login,
        logout,
        isAuthenticated,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};